import { LuMoon, LuSun } from 'react-icons/lu';
import { IconButton } from '../../design-system/components/IconButton';
import { useThemeStore } from '../../stores/theme.store';

/**
 * THE PUBLIC SITE'S SUN/MOON TOGGLE (docs/plans/DARK-MODE.md D9, §11 B8).
 * One icon button in the landing header and footer (and the /learn pages
 * that share them), two states. The icon shows the theme the page is in, a
 * stored "Match device" included (it shows what the device resolves to);
 * the label names the action, so no aria-pressed. A click stores the
 * explicit opposite in the same `ee.theme` key the app's Appearance setting
 * writes, so the choice is the one the app opens with after sign-in (same
 * origin; sign-in never touches the key).
 *
 * Always rendered: the D5 launch flag was removed 2026-10-28 (David), so a
 * production build's prerendered pages carry it too.
 */
export function ThemeToggle({ className, testId }: { className?: string; testId?: string }) {
  const resolved = useThemeStore((s) => s.resolved);
  const setPreference = useThemeStore((s) => s.setPreference);
  const dark = resolved === 'dark';
  return (
    <IconButton
      label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      icon={
        // Both, picked by CSS on the `[data-theme]` the boot script sets
        // before paint (index.css `dark:`), not by this state: the public
        // pages are prerendered in the light state, and a visitor with dark
        // stored saw the sun until React mounted (b-187). The label is
        // React's, so it follows at mount.
        <>
          <LuSun data-icon="sun" className="dark:hidden" />
          <LuMoon data-icon="moon" className="hidden dark:inline-block" />
        </>
      }
      size="sm"
      tone="ghost"
      className={className}
      data-testid={testId}
      onClick={() => setPreference(dark ? 'light' : 'dark')}
    />
  );
}

export default ThemeToggle;
